import React from 'react';
import {
  BooleanField,
  BooleanInput,
  Create,
  DateInput,
  Edit,
  EditButton,
  Filter,
  FormDataConsumer,
  maxLength,
  maxValue,
  minLength,
  minValue,
  NullableBooleanInput,
  number,
  NumberInput,
  ReferenceArrayInput,
  required,
  SearchInput,
  SelectInput,
  TextField,
  TextInput,
} from 'react-admin';

import withStyles from '@material-ui/core/styles/withStyles';
import { ZippityDatagrid, ZippitySimpleForm } from '../../components';
import CustomTitle from '../../components/CustomTitle';
import BulkDeleteButton from '../../components/BulkDeleteButton';

import TextFieldWithPrependAppend from '../TextFieldWithPrependAppend';
import PriceField from './PriceField';
import PriceInput from '../PriceInput';
import ReferenceArrayChild from '../../components/ReferenceArrayChild';
import { getZ3PConfigValue, perPage } from '../../helper';
import ListWithCSVImport from '../../components/ListWithCSVImport';

const CouponFilter = (props) => (
  <Filter {...props}>
    <SearchInput source="q" alwaysOn />
    <DateInput source="valid_on_date" />
    <NullableBooleanInput source="applies_to_full_bill" defaultValue />
  </Filter>
);

const listStyles = {};

const isUsingHelpText = getZ3PConfigValue('is_using_help_text');

export const CouponList = withStyles(listStyles)(({ classes, ...props }) => (
  <div>
    <ListWithCSVImport
      {...props}
      filters={<CouponFilter />}
      bulkActionButtons={<BulkDeleteButton />}
      sort={{ field: 'coupon_code', order: 'ASC' }}
      perPage={50}
    >
      <ZippityDatagrid>
        <TextField source="created_at" />
        <TextField source="modified_at" />
        <TextField source="coupon_code" />
        <BooleanField source="applies_to_full_bill" />
        <PriceField source="amount_off_dollars" />
        <TextFieldWithPrependAppend
          source="amount_off_percent"
          append="% off"
        />
        <TextField source="start_date" />
        <TextField source="end_date" />
        <BooleanField source="can_use_multiple_times" />
        <EditButton />
      </ZippityDatagrid>
    </ListWithCSVImport>
    {isUsingHelpText && (
      <div>
        These are the coupon codes that can be used by customers when booking a
        service.
        <ul>
          <li>There can be only one entry per coupon code.</li>
          <li>
            A coupon should never have both an Absolute Amount Off and a
            Percentage Off. If it does, we only use the absolute amount.
          </li>
          <li>
            The <strong>Coupon Code</strong> is{' '}
            <a href="/screenshots/coupon-checkout.png" target="_blank">
              what the customer enters during checkout
            </a>
            .
          </li>
          <li>
            <strong>Applies to Full Bill</strong> only matters if you select
            &apos;Valid Services&apos;. It means: if the customer orders an oil
            service, should they get 10% off just that service, or 10% off their
            entire bill? Or if they order multiple oil services, should they get
            $10 off each one, or just apply the $10 to the full bill?
          </li>
          <li>
            The <strong>Absolute Amount Off</strong> is subtracted from the
            customer&apos;s bill, if they meet the requirements (services,
            location, dates).
          </li>
          <li>
            The <strong>Percentage Off</strong> is subtracted from the
            customer&apos;s bill, if they meet the requirements (services,
            location, dates).
          </li>
          <li>
            The <strong>Start Date / End Date</strong> are restrictions on the
            date they <strong>create</strong> the booking, not on the date of
            service.
          </li>
        </ul>
      </div>
    )}
  </div>
));

const editStyles = {
  referenceArrayWide: { width: '100%' },
};

const validateCouponCode = [required(), minLength(2), maxLength(255)];
const validatePercentage = [number(), minValue(0), maxValue(100)];

const validateRequiredPercentage = [
  required(),
  number(),
  minValue(1),
  maxValue(100),
];

const APPLIES_TO_FULL_BILL_EXPLANATION =
  "Note: 'Applies to full bill' only matters if you select 'Valid Services'. " +
  "For 'Absolute Amount Off' coupons, it means: do you want the amount to apply multiple times, if the customer selects multiple valid services? " +
  "For 'Percentage Off' coupons, it means: Do you want the percentage to apply just to the valid services, or to the full bill?";

export const CouponEdit = withStyles(editStyles)(({ classes, ...props }) => (
  <Edit
    undoable={false}
    title={<CustomTitle title="resources.coupon.title" />}
    {...props}
  >
    <ZippitySimpleForm redirect={false}>
      <PriceInput source="amount_off_dollars" />
      <NumberInput validate={validatePercentage} source="amount_off_percent" />

      <DateInput source="start_date" />
      <DateInput source="end_date" />
      <ReferenceArrayInput
        source="valid_service_offering_ids"
        reference="service-offering"
        sort={{ field: 'short_name', order: 'ASC' }}
        perPage={perPage}
        className={classes.referenceArrayWide}
      >
        <ReferenceArrayChild
          {...props}
          optionText="short_name"
          source="valid_service_offering_ids"
        />
      </ReferenceArrayInput>
      <ReferenceArrayInput
        source="valid_client_location_ids"
        reference="client-location"
        sort={{ field: 'long_name', order: 'ASC' }}
        perPage={perPage}
        className={classes.referenceArrayWide}
      >
        <ReferenceArrayChild
          {...props}
          optionText="long_name"
          source="valid_client_location_ids"
        />
      </ReferenceArrayInput>

      <BooleanInput source="applies_to_full_bill" />
      <p>{APPLIES_TO_FULL_BILL_EXPLANATION}</p>
      <BooleanInput source="can_use_multiple_times" />
      <NumberInput source="minimum_tire_quantity" />
      <NumberInput source="maximum_tire_quantity" />
    </ZippitySimpleForm>
  </Edit>
));

export const CouponCreate = withStyles(editStyles)(({ classes, ...props }) => (
  <Create {...props}>
    <ZippitySimpleForm redirect="edit">
      <TextInput validate={validateCouponCode} source="coupon_code" />
      <SelectInput
        source="coupon_type"
        validate={[required()]}
        choices={[
          { id: 'absolute', name: 'Absolute' },
          { id: 'percentage', name: 'Percentage' },
        ]}
      />

      <FormDataConsumer>
        {({ formData }) => {
          if (formData.coupon_type === 'absolute') {
            return <PriceInput source="amount_off_dollars" />;
          }
          if (formData.coupon_type === 'percentage') {
            return (
              <NumberInput
                validate={validateRequiredPercentage}
                source="amount_off_percent"
              />
            );
          }
          return null;
        }}
      </FormDataConsumer>

      <DateInput source="start_date" />
      <DateInput source="end_date" />

      <ReferenceArrayInput
        source="valid_service_offering_ids"
        reference="service-offering"
        defaultValue={[]}
        sort={{ field: 'short_name', order: 'ASC' }}
        perPage={perPage}
        className={classes.referenceArrayWide}
      >
        <ReferenceArrayChild
          {...props}
          optionText="short_name"
          source="valid_service_offering_ids"
        />
      </ReferenceArrayInput>
      <ReferenceArrayInput
        source="valid_client_location_ids"
        reference="client-location"
        sort={{ field: 'long_name', order: 'ASC' }}
        defaultValue={[]}
        perPage={perPage}
        className={classes.referenceArrayWide}
      >
        <ReferenceArrayChild
          {...props}
          optionText="long_name"
          source="valid_client_location_ids"
        />
      </ReferenceArrayInput>

      <FormDataConsumer>
        {({ formData }) => {
          if (
            formData.valid_service_offering_ids &&
            formData.valid_service_offering_ids.length
          ) {
            return (
              <div>
                <BooleanInput source="applies_to_full_bill" />
                <p>{APPLIES_TO_FULL_BILL_EXPLANATION}</p>
              </div>
            );
          }
          return null;
        }}
      </FormDataConsumer>
      <BooleanInput source="can_use_multiple_times" />
      <NumberInput source="minimum_tire_quantity" />
      <NumberInput source="maximum_tire_quantity" />
    </ZippitySimpleForm>
  </Create>
));
